import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';

const FixedNewsOnboarding = ({ isOpen = true, onComplete, firestoreService }) => {  // ✅ ADD firestoreService
  const [stage, setStage] = useState('intro');
  const [typedText, setTypedText] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showButton, setShowButton] = useState(false);
  const [contentFade, setContentFade] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [startTyping, setStartTyping] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);  // ✅ ADD loading state

  const texts = {
    intro: { full: "Create your perfect audio experience", base: "Create your perfect audio ", highlight: "experience" },
    news: { full: "First, choose the sources you trust", base: "First, choose the sources you ", highlight: "trust", rest: "" },
    local: { full: "Get briefings and local news updates delivered daily", base: "Get briefings and ", highlight: "local news updates", rest: " delivered daily" },
    companies: { full: "Then, follow companies that matter to you", base: "Then, follow companies that ", highlight: "matter to you", rest: "" },
    final: { full: "Enjoy and Listen to Audio You Care About", base: "", highlight: "", rest: "" }
  };

  const newsLogos = [
    { id: 'abc', logo: 'https://abc.com/favicon.ico' },
    { id: 'techcrunch', logo: 'https://techcrunch.com/wp-content/uploads/2015/02/cropped-cropped-favicon-gradient.png' },
    { id: 'cnn', logo: 'https://www.cnn.com/favicon.ico' },
  ];

  const companyLogos = [
    { id: 'apple', logos: ['https://www.apple.com/favicon.ico', ] },
    { id: 'google', logos: ['https://www.google.com/favicon.ico', ] },
    { id: 'tesla', logos: ['https://upload.wikimedia.org/wikipedia/commons/thumb/e/e8/Tesla_logo.png/1200px-Tesla_logo.png', ] },
  ];

  // ✅ CHECK FIRESTORE on mount
  useEffect(() => {
    const checkCompletionStatus = async () => {
      try {
        if (firestoreService?.isReady?.()) {
          const userData = await firestoreService.getUserData();
          const hasCompleted = userData?.settings?.hasCompletedNewsOnboarding;
          
          console.log('🔍 News Onboarding check:', { hasCompleted });
          
          if (hasCompleted) {
            setIsClosing(true);
            setTimeout(() => {
              onComplete && onComplete({});
            }, 0);
          } else {
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error checking news onboarding status:', error);
        setIsLoading(false);
      }
    };

    checkCompletionStatus();
  }, [firestoreService, onComplete]);

  // Handle stage changes
  useEffect(() => {
    setTypedText('');
    setStartTyping(false);
    
    if (stage === 'local') {
      setShowCards(true);
      setTimeout(() => {
        setStartTyping(true);
      }, 600);
    } else {
      setStartTyping(true);
    }
  }, [stage]);

  // Typing animation
  useEffect(() => {
    if (!startTyping) return;
    
    const text = texts[stage];
    if (!text) return;

    let idx = 0;
    const interval = setInterval(() => {
      if (idx <= text.full.length) {
        setTypedText(text.full.substring(0, idx));
        idx++;
      } else {
        clearInterval(interval);
        handleTypingComplete();
      }
    }, 40);

    return () => clearInterval(interval);
  }, [startTyping, stage]);

  const transitionToNext = () => {
    setShowButton(false);
    setSelectedItems([]);
    setShowCards(false);
    setContentFade(true);
    
    setTimeout(() => {
      if (stage === 'intro') setStage('news');
      else if (stage === 'news') setStage('local');
      else if (stage === 'local') setStage('companies');
      else if (stage === 'companies') setStage('final');
      
      setContentFade(false);
    }, 400);
  };

  const handleTypingComplete = () => {
    if (stage === 'intro') {
      setTimeout(() => transitionToNext(), 1000);
    } else if (stage === 'news') {
      setTimeout(() => animateIcons(), 800);
    } else if (stage === 'local') {
      setTimeout(() => setShowButton(true), 600);
    } else if (stage === 'companies') {
      setTimeout(() => animateIcons(), 800);
    } else if (stage === 'final') {
      setTimeout(async () => {  // ✅ UPDATED to async
        setIsClosing(true);
        
        // ✅ SAVE TO FIRESTORE before completing
        try {
          if (firestoreService?.isReady?.()) {
            await firestoreService.updateUserData({
              'settings.hasCompletedNewsOnboarding': true,
              'settings.newsOnboardingCompletedAt': new Date().toISOString()
            });
            console.log('✅ News onboarding saved to Firestore');
          }
        } catch (error) {
          console.error('❌ Failed to save news onboarding to Firestore:', error);
        }
        
        setTimeout(() => {
          onComplete && onComplete({});
        }, 800);
      }, 1200);
    }
  };

  const animateIcons = () => {
    const items = stage === 'news' ? newsLogos : companyLogos;
    setSelectedItems([]);
    
    items.forEach((item, idx) => {
      setTimeout(() => {
        setSelectedItems(prev => [...prev, item.id]);
      }, (idx + 1) * 300);
    });

    setTimeout(() => {
      if (stage === 'news') {
        transitionToNext();
      } else if (stage === 'companies') {
        setShowButton(true);
      }
    }, items.length * 300 + 400);
  };

  const LogoImage = ({ logos }) => {
    const [idx, setIdx] = useState(0);
    const [failed, setFailed] = useState(false);
    if (failed) return <div style={{ width: '100%', height: '100%', background: '#f3f4f6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#9ca3af' }}>?</div>;
    return <img src={logos[idx]} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={() => idx + 1 < logos.length ? setIdx(idx + 1) : setFailed(true)} />;
  };

  const renderTypedText = () => {
    const text = texts[stage];
    if (!text) return null;

    if (stage === 'final') {
      return (
        <>
          {typedText.split(' ').map((w, i) => (
            <span key={i} className="word">{w}{i < typedText.split(' ').length - 1 ? ' ' : ''}</span>
          ))}
          {typedText.length < text.full.length && <span className="cursor">|</span>}
        </>
      );
    }

    const { base, highlight, rest } = text;
    
    if (typedText.length <= base.length) {
      return (
        <>
          {typedText.split(' ').map((w, i) => (
            <span key={i} className="word">{w}{i < typedText.split(' ').length - 1 ? ' ' : ''}</span>
          ))}
          <span className="cursor">|</span>
        </>
      );
    }
    
    if (typedText.length <= base.length + highlight.length) {
      const h = typedText.substring(base.length);
      return (
        <>
          {base.split(' ').map((w, i) => (
            <span key={i} className="word">{w}{i < base.split(' ').length - 1 ? ' ' : ''}</span>
          ))}
          <span className="highlight word">{h}</span>
          <span className="cursor">|</span>
        </>
      );
    }
    
    const r = typedText.substring(base.length + highlight.length);
    return (
      <>
        {base.split(' ').map((w, i) => (
          <span key={i} className="word">{w}{i < base.split(' ').length - 1 ? ' ' : ''}</span>
        ))}
        <span className="highlight word">{highlight}</span>
        <span className="word">{r}</span>
        {typedText.length < text.full.length && <span className="cursor">|</span>}
      </>
    );
  };

  if (!isOpen || isLoading) return null;  // ✅ UPDATED: Don't show if loading

  const showIcons = (stage === 'news' || stage === 'companies') && typedText === texts[stage].full;
  const showLocalCards = stage === 'local' && showCards;
  const isIntroOrFinal = stage === 'intro' || stage === 'final';

  return (
    <>
      <div className={`overlay ${isClosing ? 'closing' : ''}`}>
        <div className={`modal ${isClosing ? 'closing' : ''}`}>
          <div className={`content ${contentFade ? 'content-fade' : ''}`}>
            
            {startTyping && (
              <div className={`text-section ${isIntroOrFinal ? 'centered' : ''}`}>
                <div className={`main-text ${isIntroOrFinal ? 'intro-size' : ''}`}>
                  {renderTypedText()}
                </div>
              </div>
            )}

            {showIcons && (
              <div className="icons-grid">
                {stage === 'news' && newsLogos.map((item, idx) => (
                  <div key={item.id} className="icon-wrapper">
                    <div 
                      className={`icon-circle ${selectedItems.includes(item.id) ? 'selected' : ''}`}
                      style={{ animationDelay: `${idx * 0.15}s` }}
                    >
                      <img src={item.logo} alt="" onError={(e) => e.target.style.display = 'none'} />
                    </div>
                    {selectedItems.includes(item.id) && (
                      <div className="check-overlay">
                        <Check size={28} strokeWidth={3} color="black" />
                      </div>
                    )}
                  </div>
                ))}
                {stage === 'companies' && companyLogos.map((item, idx) => (
                  <div key={item.id} className="icon-wrapper">
                    <div 
                      className={`icon-circle ${selectedItems.includes(item.id) ? 'selected' : ''}`}
                      style={{ animationDelay: `${idx * 0.15}s` }}
                    >
                      <LogoImage logos={item.logos} />
                    </div>
                    {selectedItems.includes(item.id) && (
                      <div className="check-overlay">
                        <Check size={28} strokeWidth={3} color="black" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {showLocalCards && (
              <div className="cards-container">
                <div className="card-stack">
                  <div className="card third-card">
                    <img src="/image/intro11.png" alt="" onError={(e) => e.target.style.display = 'none'} />
                  </div>
                  <div className="card back-card">
                    <img src="/image/intro15.png" alt="" onError={(e) => e.target.style.display = 'none'} />
                  </div>
                  <div className="card front-card">
                    <img src="/image/intro21.png" alt="" onError={(e) => e.target.style.display = 'none'} />
                  </div>
                </div>
              </div>
            )}

            {showButton && (
              <button className="next-btn" onClick={transitionToNext}>
                <span>{stage === 'local' ? 'Next' : 'Finish'}</span>
              </button>
            )}

            {(stage === 'local' || stage === 'companies') && (
              <div className="onboarding-progress-indicators">
                <div className={`progress-indicator ${stage === 'local' ? 'indicator-active' : ''}`}></div>
                <div className={`progress-indicator ${stage === 'companies' ? 'indicator-active' : ''}`}></div>
              </div>
            )}
          </div>
        </div>
      </div>



<style jsx>{`
  * { box-sizing: border-box; margin: 0; padding: 0; }
  
  /* ============================================
     OPENING ANIMATIONS
     ============================================ */
 .modal {
  width: 100%;
  max-width: 420px;
  height: 85vh;
  background: linear-gradient(170deg, #fefefe, #fefefe 60%, #e0e7fc);
  border-radius: 30px 30px 0 0;
  padding: 16px;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
  animation: modalSlideUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  overflow: hidden;
  pointer-events: none; // ✅ ADDED: Prevents modal from blocking clicks on parent
  will-change: transform, opacity; // ✅ ADDED: Optimizes animation performance
}

.modal.closing {
  pointer-events: none; // ✅ ADDED: Keep disabled during close too
}

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 99999;
  animation: overlayFadeIn 0.5s ease forwards;
  pointer-events: auto; // ✅ ADDED: Overlay captures clicks, not modal
}

.content {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
  padding: 20px;
  transition: opacity 0.4s ease, filter 0.4s ease;
  pointer-events: auto; // ✅ ADDED: Content is clickable
}
  
  /* ============================================
     CLOSING ANIMATIONS (smooth fade only)
     ============================================ */
  
  .overlay.closing {
    animation: overlayFadeOut 0.8s ease forwards;
  }
  
  .modal.closing {
    animation: modalFadeOut 0.8s ease forwards;
  }
  
  /* ============================================
     OPENING KEYFRAMES
     ============================================ */
  
  @keyframes overlayFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
.modal {
  width: 100%;
  max-width: 420px;
  height: 85vh;
  background: linear-gradient(170deg, #fefefe, #fefefe 60%, #e0e7fc);
  border-radius: 30px 30px 0 0;
  padding: 16px;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
  animation: modalFadeIn 0.5s ease forwards; // ✅ FIXED: Just fade in, no slide
  overflow: hidden;
  pointer-events: none;
  will-change: transform, opacity;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
  /* ============================================
     CLOSING KEYFRAMES (smooth fade - no slide)
     ============================================ */
  
  @keyframes overlayFadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
      backdrop-filter: blur(0px);
    }
  }
  
  @keyframes modalFadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
  
  /* ============================================
     OTHER ANIMATIONS
     ============================================ */
  
  .content {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    padding: 20px;
    transition: opacity 0.4s ease, filter 0.4s ease;
  }
  
  .content.content-fade {
    opacity: 0;
    filter: blur(8px);
  }
  
  .text-section {
    width: 100%;
    transition: all 0.3s ease;
  }
  
  .text-section.centered {
    text-align: center;
  }
  
  .main-text {
    font-size: 23px;
    font-weight: 500;
    color: #1f2937;
    line-height: 1.4;
    text-align: center;
    min-height: 40px;
    animation: textSlideUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .main-text.intro-size {
    font-size: 23px;
    max-width: 300px;
    margin: 0 auto;
  }
  
  .word {
    display: inline;
    animation: wordFade 0.4s ease, wordBounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .highlight {
    color: #3b82f6;
  }
  
  .cursor {
    margin-left: 3px;
    animation: blink 0.8s infinite;
  }
  
  .icons-grid {
    display: flex;
    gap: 24px;
    justify-content: center;
    align-items: center;
    animation: slideIn 0.5s ease;
  }
  
  .icon-wrapper {
    position: relative;
    width: 57px;
    height: 57px;
  }
  
  .icon-circle {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    background: white;
    border: 1px solid #818181ff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    opacity: 0;
    transform: scale(0.8);
  }
  
  .icon-circle img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
  
  .icon-circle.selected {
    border-color: #000;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    animation: iconFade 0.4s ease forwards;
  }
  
  .check-overlay {
    position: absolute;
    inset: 0;
    background: #ffffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: checkPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    z-index: 10;
    border: 1.5px solid #818181ff;
  }
  
  .cards-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .card-stack {
    position: relative;
    width: 100px;
    height: 100px;
    perspective: 1200px;
  }
  
  .card {
    position: absolute;
    width: 80px;
    height: 80px;
    border-radius: 12px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }
  
  .card img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .third-card {
    bottom: 0;
    right: 12px;
    z-index: 0;
    transform: rotateX(5deg) rotateY(2deg) rotateZ(2deg);
    animation: cardSlide0 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }
  
  .back-card {
    bottom: 6px;
    right: 6px;
    z-index: 1;
    animation: cardSlide1 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }
  
  .front-card {
    top: 11px;
    left: 0;
    z-index: 2;
    transform: rotateX(10deg) rotateY(-3deg) rotateZ(-3deg);
    transform-style: preserve-3d;
    border: 2px solid rgba(185, 185, 185, 0.06);
    animation: cardSlide2 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s both;
  }
  
  .next-btn {
    width: 100%;
    max-width: 350px;
    padding: 11px;
    background: #000;
    color: white;
    border: none;
    border-radius: 28px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    animation: btnFade 0.4s ease, btnPulse 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
  }
  
  .next-btn::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  .next-btn:hover::before {
    width: 300px;
    height: 300px;
  }
  
  .next-btn:hover {
    background: #1a1a1a;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  }
  
  .next-btn:active {
    transform: translateY(0) scale(0.98);
  }
  
  .next-btn span {
    position: relative;
    z-index: 1;
  }
  
  .onboarding-progress-indicators {
    display: flex;
    gap: 6px;
    animation: fadeIn 0.4s ease;
    padding: 8px 12px;
    border-radius: 20px;
    backdrop-filter: blur(8px);
  }
  
  .progress-indicator {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(183, 183, 183, 0.64);
    transition: all 0.3s ease;
  }
  
  .progress-indicator.indicator-active {
    width: 20px;
    border-radius: 3px;
    background: #000;
  }
  
  /* ============================================
     ALL REMAINING KEYFRAMES
     ============================================ */
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes wordFade {
    from {
      opacity: 0;
      filter: blur(4px);
    }
    to {
      opacity: 1;
      filter: blur(0);
    }
  }
  
  @keyframes blink {
    0%, 49% { opacity: 1; }
    50%, 100% { opacity: 0; }
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes popIn {
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes iconFade {
    to {
      opacity: 0;
      transform: scale(0.9);
    }
  }
  
  @keyframes checkPop {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes btnFade {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes cardSlide0 {
    from {
      opacity: 0;
      transform: translateX(-30px) translateY(30px) rotateX(5deg) rotateY(2deg) rotateZ(2deg);
    }
    to {
      opacity: 1;
      transform: translateX(0) translateY(0) rotateX(5deg) rotateY(2deg) rotateZ(2deg);
    }
  }
  
  @keyframes cardSlide1 {
    from {
      opacity: 0;
      transform: translateX(-20px) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0) translateY(0);
    }
  }
  
  @keyframes cardSlide2 {
    from {
      opacity: 0;
      transform: translateX(20px) translateY(-20px) rotateX(10deg) rotateY(-3deg) rotateZ(-3deg);
    }
    to {
      opacity: 1;
      transform: translateX(0) translateY(0) rotateX(10deg) rotateY(-3deg) rotateZ(-3deg);
    }
  }
  
  @keyframes textSlideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes wordBounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
  }
  
  @keyframes btnPulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.03); }
    100% { transform: scale(1); }
  }
`}</style>
    </>
  );
};

export default FixedNewsOnboarding;